import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { NewsItem } from '../../types/news';

const cardMdComponents = {
  p: (p: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="text-mafia-paper-dim text-sm leading-relaxed line-clamp-3" {...p} />
  ),
  strong: (p: React.HTMLAttributes<HTMLElement>) => (
    <strong className="text-mafia-paper font-semibold" {...p} />
  ),
  em: (p: React.HTMLAttributes<HTMLElement>) => <em {...p} />,
  // Flatten headings — card is too small for big heading styles
  h1: (p: React.HTMLAttributes<HTMLElement>) => (
    <p className="text-mafia-paper text-sm font-semibold leading-relaxed line-clamp-3" {...p} />
  ),
  h2: (p: React.HTMLAttributes<HTMLElement>) => (
    <p className="text-mafia-paper text-sm font-semibold leading-relaxed line-clamp-3" {...p} />
  ),
  h3: (p: React.HTMLAttributes<HTMLElement>) => (
    <p className="text-mafia-paper-dim text-sm font-semibold leading-relaxed line-clamp-3" {...p} />
  ),
  ul: (p: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="text-mafia-paper-dim text-sm list-disc list-inside line-clamp-3" {...p} />
  ),
  ol: (p: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="text-mafia-paper-dim text-sm list-decimal list-inside line-clamp-3" {...p} />
  ),
  // Strip block elements that don't fit in a card preview
  blockquote: (p: React.HTMLAttributes<HTMLElement>) => (
    <p className="text-mafia-paper-dim text-sm italic leading-relaxed line-clamp-3" {...p} />
  ),
  code: (p: React.HTMLAttributes<HTMLElement>) => (
    <code className="text-mafia-gold font-mono text-xs" {...p} />
  ),
  a: (p: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <span className="text-mafia-gold">{p.children}</span>
  ),
};

interface NewsCardProps {
  item: NewsItem;
}

export function NewsCard({ item }: NewsCardProps) {
  const date = new Date(item.publishedAt).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Accumulate paragraphs until we have enough text for a card preview
  const paragraphs = item.teaser.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  let preview = '';
  for (const para of paragraphs) {
    preview += (preview ? '\n\n' : '') + para;
    if (preview.length >= 150) break;
  }

  return (
    <Link
      to={`/news/${item.newsId}`}
      className="card-vintage block overflow-hidden hover:bg-mafia-smoke transition-colors group"
    >
      <div className="p-5">
        <p className="font-mono text-mafia-muted text-xs tracking-widest uppercase mb-2">{date}</p>
        <h3 className="font-display text-mafia-paper font-semibold text-lg leading-snug mb-3 line-clamp-2 group-hover:text-mafia-gold transition-colors">
          {item.title}
        </h3>
        <Markdown remarkPlugins={[remarkGfm]} components={cardMdComponents}>
          {preview}
        </Markdown>
        <p className="mt-3 font-mono text-mafia-gold text-xs tracking-wider uppercase">
          Читать →
        </p>
      </div>
    </Link>
  );
}
